import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Callout, Modal, More } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import {
  adjustmentProblem,
  grossOf,
  parseAdjustment,
  previewNet,
  sanitiseAdjustment,
  severityPpm,
} from './line-adjust-math.js';
import type { AdjustMode, AdjustableLine } from './line-adjust-math.js';
import './line-adjust.css';

/**
 * Changing what a line costs (SCOPE §8.1).
 *
 * Budget: **price override on a line in ≤3 interactions** — tap the line, tap
 * *Set price*, type the value and press Enter. A percentage discount, the
 * commoner move by far, takes two, because *% off* is already selected when the
 * dialog opens (§2.2-5, smart defaults).
 *
 * Everything here is a guardrail rather than a roadblock (§2-6). Steeper than
 * the tenant's threshold is *allowed*; it wants a manager's PIN, entered
 * without anyone signing out and recorded against the row. The threshold is
 * known on the client too, so the dialog says a manager will be needed *before*
 * the button is pressed rather than explaining it afterwards (§2.2-7).
 *
 * The arithmetic lives in `line-adjust-math.ts` and runs on the same
 * `@sitzmark/core` primitives the server does, so the preview is the receipt.
 */

type SaleView = Awaited<ReturnType<typeof api.register.setLineDiscount.mutate>>;

const MODES: ReadonlyArray<{ mode: AdjustMode; label: string; hint: string }> = [
  { mode: 'percent', label: '% off', hint: 'A percentage off this line' },
  { mode: 'amount', label: '$ off', hint: 'A dollar amount off the whole line' },
  { mode: 'price', label: 'Set price', hint: 'A new unit price' },
];

/**
 * Why the price moved, in one tap.
 *
 * §8.1 wants a reason captured; the step budget will not pay for typing one.
 * Nothing is pre-selected, because a wrong reason recorded by a blind Enter is
 * worse than a general one — so the default is honest about being general and
 * the specific answers cost a single tap.
 */
const REASONS = ['Price match', 'Damaged', 'Display model', 'Employee purchase'] as const;
const DEFAULT_REASON = 'Adjusted at register';

export type { AdjustableLine };

export function LineAdjustDialog({
  saleId,
  line,
  thresholdPpm,
  onClose,
  onSaleChanged,
}: {
  saleId: string;
  line: AdjustableLine;
  /** Above this, a manager approves. Per-tenant config (SCOPE §18.1-4). */
  thresholdPpm: number;
  onClose: () => void;
  onSaleChanged: (sale: SaleView) => void;
}) {
  const [mode, setMode] = useState<AdjustMode>('percent');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState(line.note ?? '');
  const [needsApproval, setNeedsApproval] = useState(false);

  const parsed = parseAdjustment(mode, value);
  const problem = parsed === null ? undefined : adjustmentProblem(mode, parsed, line);
  const preview = parsed === null || problem !== undefined ? null : previewNet(mode, parsed, line);
  const overThreshold = parsed !== null && severityPpm(mode, parsed, line) > thresholdPpm;
  const canApply = parsed !== null && problem === undefined;
  const noteChanged = note.trim() !== (line.note ?? '');

  const apply = useMutation({
    mutationFn: (override?: ManagerOverride) => {
      if (parsed === null) throw new Error('Enter an amount');
      const approval = override === undefined ? {} : { override };

      if (mode === 'price') {
        return api.register.overridePrice.mutate({
          saleId,
          lineId: line.id,
          unitPriceCents: parsed,
          reason: reason ?? DEFAULT_REASON,
          ...approval,
        });
      }

      return api.register.setLineDiscount.mutate({
        saleId,
        lineId: line.id,
        discount:
          mode === 'percent'
            ? { kind: 'percent', percent: parsed }
            : { kind: 'amount', amountCents: parsed },
        reason,
        ...approval,
      });
    },
    onSuccess: async (view) => {
      recordBreadcrumb('register', `adjusted a line (${mode})`);
      // A note typed in the same visit is saved with the adjustment rather than
      // silently dropped — the operator changed both, so both are their intent.
      const final = noteChanged
        ? await api.register.setLineNote.mutate({ saleId, lineId: line.id, note: note.trim() })
        : view;
      onSaleChanged(final);
      onClose();
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  const clear = useMutation({
    mutationFn: async () => {
      const view = await api.register.setLineDiscount.mutate({
        saleId,
        lineId: line.id,
        discount: { kind: 'none' },
        reason: null,
      });
      // An override is a changed unit price, not a discount rule, so clearing
      // it means putting the original price back.
      return line.priceOverridden && line.originalUnitPriceCents !== null
        ? api.register.overridePrice.mutate({
            saleId,
            lineId: line.id,
            unitPriceCents: line.originalUnitPriceCents,
            reason: 'Override removed',
          })
        : view;
    },
    onSuccess: (view) => {
      onSaleChanged(view);
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.register.removeLine.mutate({ saleId, lineId: line.id }),
    onSuccess: (view) => {
      recordBreadcrumb('register', 'removed a line');
      onSaleChanged(view);
      onClose();
    },
  });

  const saveNote = useMutation({
    mutationFn: () =>
      api.register.setLineNote.mutate({ saleId, lineId: line.id, note: note.trim() }),
    onSuccess: (view) => {
      onSaleChanged(view);
      onClose();
    },
  });

  if (needsApproval) {
    return (
      <ManagerApproval
        title="Manager approval needed"
        because={`${describe(mode, parsed, line)} on ${line.description}.`}
        busy={apply.isPending}
        error={apply.isError ? approvalRefusal(apply.error) : null}
        onCancel={onClose}
        onApprove={(override) => apply.mutate(override)}
      />
    );
  }

  const submit = () => {
    if (canApply) apply.mutate(undefined);
    else if (noteChanged) saveNote.mutate();
  };

  return (
    <Modal
      open
      title={line.description}
      description={`${line.quantity} × ${money(line.unitPriceCents)} · currently ${money(line.netCents)}`}
      onClose={onClose}
      secondaryAction={
        <>
          {/* Destructive, and as far from Apply as this frame allows (§2.2-1). */}
          <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
            Remove
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Back to cart
          </Button>
          {/* Offered only when there is something to undo (§2.2-13). */}
          {line.lineDiscountCents > 0 || line.priceOverridden ? (
            <Button variant="ghost" busy={clear.isPending} onClick={() => clear.mutate()}>
              Clear
            </Button>
          ) : null}
        </>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={apply.isPending || saveNote.isPending}
          disabledReason={
            canApply || (value === '' && noteChanged)
              ? undefined
              : value === ''
                ? 'Enter an amount'
                : (problem ?? 'That is not a number')
          }
          onClick={submit}
        >
          {canApply ? 'Apply' : 'Save note'}
        </Button>
      }
    >
      <div className="adjust__modes" role="group" aria-label="What kind of adjustment">
        {MODES.map((option) => (
          <button
            key={option.mode}
            type="button"
            className={`adjust__mode${mode === option.mode ? ' adjust__mode--active' : ''}`}
            aria-pressed={mode === option.mode}
            title={option.hint}
            onClick={() => setMode(option.mode)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="adjust-value">
          {mode === 'percent'
            ? 'Percent off'
            : mode === 'amount'
              ? // Off the whole line, however many are on it — say so where it
                // matters, or two units read as two discounts (FB-1092).
                line.quantity > 1
                ? `Dollars off the line (${line.quantity} items)`
                : 'Dollars off'
              : 'New unit price'}
        </label>
        <input
          id="adjust-value"
          className="input money"
          inputMode="decimal"
          autoFocus
          placeholder={mode === 'percent' ? '10' : '0.00'}
          value={value}
          onChange={(event) => setValue(sanitiseAdjustment(event.target.value))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
        />
      </div>

      {preview !== null ? (
        <div className="adjust__preview">
          <span className="text-secondary">This line becomes</span>
          <strong className="money adjust__preview-amount">{money(preview)}</strong>
          {preview < grossOf(line) ? (
            <span className="text-secondary money">−{money(grossOf(line) - preview)}</span>
          ) : null}
          {/* Said before the press, not after the refusal (§2.2-7). */}
          {overThreshold ? <Badge tone="warning">A manager approves this</Badge> : null}
        </div>
      ) : null}

      <div className="adjust__reasons" role="group" aria-label="Why">
        {REASONS.map((option) => (
          <button
            key={option}
            type="button"
            className={`chip${reason === option ? ' chip--on' : ''}`}
            aria-pressed={reason === option}
            onClick={() => setReason(reason === option ? null : option)}
          >
            {option}
          </button>
        ))}
      </div>

      <More label="Note on this line">
        <input
          className="input"
          maxLength={200}
          placeholder="Ordered in blue · collecting Friday"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </More>

      {apply.isError && !isOverridable(apply.error) ? (
        <Callout tone="danger">{errorMessage(apply.error)}</Callout>
      ) : null}
      {remove.isError ? <Callout tone="danger">{errorMessage(remove.error)}</Callout> : null}
      {clear.isError ? <Callout tone="danger">{errorMessage(clear.error)}</Callout> : null}
    </Modal>
  );
}

function describe(mode: AdjustMode, parsed: number | null, line: AdjustableLine): string {
  if (parsed === null) return 'This adjustment';
  if (mode === 'percent') return `${parsed}% off`;
  if (mode === 'amount') return `${money(parsed)} off`;
  return `${money(line.unitPriceCents)} down to ${money(parsed)}`;
}

// ---------------------------------------------------------------------------

/**
 * A discount across the whole sale (SCOPE §8.1).
 *
 * Same shape as the line dialog minus *Set price*, which has no meaning for a
 * basket. The engine allocates the result back down across the lines by weight,
 * which is what keeps a later partial return reversing exactly what was charged.
 */
export function SaleDiscountDialog({
  saleId,
  grossSubtotalCents,
  hasDiscount,
  thresholdPpm,
  onClose,
  onSaleChanged,
}: {
  saleId: string;
  grossSubtotalCents: number;
  hasDiscount: boolean;
  thresholdPpm: number;
  onClose: () => void;
  onSaleChanged: (sale: SaleView) => void;
}) {
  const [mode, setMode] = useState<Exclude<AdjustMode, 'price'>>('percent');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState<string | null>(null);
  const [needsApproval, setNeedsApproval] = useState(false);

  const basket = { unitPriceCents: grossSubtotalCents, quantity: 1 };
  const parsed = parseAdjustment(mode, value);
  const problem = parsed === null ? undefined : adjustmentProblem(mode, parsed, basket);
  const preview =
    parsed === null || problem !== undefined ? null : previewNet(mode, parsed, basket);
  const overThreshold = parsed !== null && severityPpm(mode, parsed, basket) > thresholdPpm;
  const canApply = parsed !== null && problem === undefined;

  const apply = useMutation({
    mutationFn: (override?: ManagerOverride) => {
      if (parsed === null) throw new Error('Enter an amount');
      return api.register.setSaleDiscount.mutate({
        saleId,
        discount:
          mode === 'percent'
            ? { kind: 'percent', percent: parsed }
            : { kind: 'amount', amountCents: parsed },
        reason,
        ...(override === undefined ? {} : { override }),
      });
    },
    onSuccess: (view) => {
      recordBreadcrumb('register', 'discounted the sale');
      onSaleChanged(view);
      onClose();
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  const clear = useMutation({
    mutationFn: () =>
      api.register.setSaleDiscount.mutate({ saleId, discount: { kind: 'none' }, reason: null }),
    onSuccess: (view) => {
      onSaleChanged(view);
      onClose();
    },
  });

  if (needsApproval) {
    return (
      <ManagerApproval
        title="Manager approval needed"
        because={`${mode === 'percent' ? `${parsed}%` : money(parsed ?? 0)} off the whole sale.`}
        busy={apply.isPending}
        error={apply.isError ? approvalRefusal(apply.error) : null}
        onCancel={onClose}
        onApprove={(override) => apply.mutate(override)}
      />
    );
  }

  return (
    <Modal
      open
      title="Discount the sale"
      description={`Applied across every line. Subtotal is ${money(grossSubtotalCents)}.`}
      onClose={onClose}
      secondaryAction={
        <>
          <Button variant="ghost" onClick={onClose}>
            Back to cart
          </Button>
          {hasDiscount ? (
            <Button variant="ghost" busy={clear.isPending} onClick={() => clear.mutate()}>
              Clear
            </Button>
          ) : null}
        </>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={apply.isPending}
          disabledReason={
            canApply
              ? undefined
              : value === ''
                ? 'Enter an amount'
                : (problem ?? 'That is not a number')
          }
          onClick={() => apply.mutate(undefined)}
        >
          Apply
        </Button>
      }
    >
      <div className="adjust__modes" role="group" aria-label="What kind of discount">
        {MODES.filter((option) => option.mode !== 'price').map((option) => (
          <button
            key={option.mode}
            type="button"
            className={`adjust__mode${mode === option.mode ? ' adjust__mode--active' : ''}`}
            aria-pressed={mode === option.mode}
            onClick={() => setMode(option.mode as 'percent' | 'amount')}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="sale-discount-value">
          {mode === 'percent' ? 'Percent off' : 'Dollars off'}
        </label>
        <input
          id="sale-discount-value"
          className="input money"
          inputMode="decimal"
          autoFocus
          placeholder={mode === 'percent' ? '10' : '0.00'}
          value={value}
          onChange={(event) => setValue(sanitiseAdjustment(event.target.value))}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && canApply) {
              event.preventDefault();
              apply.mutate(undefined);
            }
          }}
        />
      </div>

      {preview !== null ? (
        <div className="adjust__preview">
          <span className="text-secondary">Subtotal becomes</span>
          <strong className="money adjust__preview-amount">{money(preview)}</strong>
          <span className="text-secondary money">−{money(grossSubtotalCents - preview)}</span>
          {overThreshold ? <Badge tone="warning">A manager approves this</Badge> : null}
        </div>
      ) : null}

      <div className="adjust__reasons" role="group" aria-label="Why">
        {REASONS.map((option) => (
          <button
            key={option}
            type="button"
            className={`chip${reason === option ? ' chip--on' : ''}`}
            aria-pressed={reason === option}
            onClick={() => setReason(reason === option ? null : option)}
          >
            {option}
          </button>
        ))}
      </div>

      {apply.isError && !isOverridable(apply.error) ? (
        <Callout tone="danger">{errorMessage(apply.error)}</Callout>
      ) : null}
    </Modal>
  );
}
